import { UnseenReviewBar } from "@/components/admin/UnseenReviewBar";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useWaitingItems, WaitingItem } from "@/hooks/useWaitingItems";
import { toast } from "@/hooks/use-toast";
import { friendlyError } from "@/lib/friendlyError";
import {
  Bell, Check, Calendar as CalendarIcon, ExternalLink, ArrowRightLeft,
  Copy, Plus, Search, Sparkles,
} from "lucide-react";
import { WorkflowDraftsWidget } from "@/components/assistant/WorkflowDraftsWidget";
import {
  ExecutivePage, PageHero, ThreeQuestions, ExecutiveSummary,
  DecisionCard, QuietSection, QuietEmptyState, GardenStatusBadge,
  type SummaryStat,
} from "@/components/executive/system";
import { findStaleWaitingItems, closeStaleWaitingItems } from "@/lib/waitingReconcile";

const PERSON_TYPES = [
  "parent", "instructor", "vendor", "student", "staff",
  "church_partner", "community_partner", "other",
];
const DAY = 24 * 60 * 60 * 1000;
const ACTIVE = new Set([
  "waiting", "reminder_scheduled", "reminder_sent",
  "response_received", "escalated", "overdue",
]);

// ────────────────────────── derivation helpers

function isOverdue(i: WaitingItem) {
  if (i.status === "escalated" || i.status === "overdue") return true;
  return !!i.expected_response_date &&
    new Date(i.expected_response_date).getTime() < Date.now();
}
function daysUntilDue(i: WaitingItem): number | null {
  if (!i.expected_response_date) return null;
  return Math.round(
    (new Date(i.expected_response_date).getTime() - Date.now()) / DAY,
  );
}
function daysSince(iso: string) {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DAY));
}
function isBlocker(i: WaitingItem) {
  const c = (i.category ?? "").toLowerCase();
  return (
    c.includes("payroll") || c.includes("w9") || c.includes("w-9") ||
    c.includes("tax") || c.includes("payment") || c.includes("registration") ||
    c.includes("agreement") || c.includes("background") || c.includes("launch") ||
    !!i.related_class_id || !!i.related_registration_id
  );
}
function isWaitingOnMe(i: WaitingItem) {
  const p = i.person_type;
  return p === "staff" || (i.category ?? "").toLowerCase().includes("me");
}
function relatedHref(i: WaitingItem) {
  if (i.related_vendor_id) return `/admin/popup?vendor=${i.related_vendor_id}`;
  if (i.related_instructor_id) return `/admin/instructors?instructor=${i.related_instructor_id}`;
  if (i.related_registration_id) return `/admin/registrations?registration=${i.related_registration_id}`;
  if (i.related_proposal_id) return `/admin/instructors?proposal=${i.related_proposal_id}`;
  if (i.related_class_id) return `/admin/classes?class=${i.related_class_id}`;
  return null;
}
function ownerLabel(i: WaitingItem) {
  if (isWaitingOnMe(i)) return "You";
  return i.person_name;
}

type GroupKey = "needs_me" | "waiting_others" | "due_soon" | "finished" | "history";

function assignGroup(i: WaitingItem): GroupKey {
  if (i.status === "completed" || i.status === "cancelled") {
    const done = i.completed_at ?? i.updated_at;
    return daysSince(done) <= 7 ? "finished" : "history";
  }
  if (isOverdue(i) || isWaitingOnMe(i)) return "needs_me";
  const d = daysUntilDue(i);
  if (d !== null && d <= 3) return "due_soon";
  return "waiting_others";
}

// ────────────────────────── page

export default function WaitingPage() {
  const { items, loading, refresh, stats } = useWaitingItems();
  const [search, setSearch] = useState("");
  const [personFilter, setPersonFilter] = useState<string>("all");
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useUrlItemFocus();
  const [staleCount, setStaleCount] = useState(0);
  const [reconciling, setReconciling] = useState(false);

  // Reconcile against source records on load + when items change.
  useEffect(() => {
    if (!items.length) { setStaleCount(0); return; }
    let cancelled = false;
    findStaleWaitingItems(items).then((r) => {
      if (!cancelled) setStaleCount(r.length);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [items]);

  const closeStale = async () => {
    setReconciling(true);
    try {
      const stale = await findStaleWaitingItems(items);
      const n = await closeStaleWaitingItems(stale);
      toast({ title: `${n} item${n === 1 ? "" : "s"} closed`, description: "Source records already completed these." });
      refresh();
    } catch (e: any) {
      toast({ title: "Couldn't reconcile", description: e?.message, variant: "destructive" });
    } finally { setReconciling(false); }
  };

  const active = useMemo(() => items.filter((i) => ACTIVE.has(i.status)), [items]);
  const overdue = useMemo(() => active.filter(isOverdue), [active]);
  const waitingOnMe = useMemo(() => active.filter(isWaitingOnMe), [active]);
  const noFollowUp = useMemo(() => active.filter((i) => !i.expected_response_date), [active]);
  const finishedRecently = useMemo(
    () => items.filter((i) => i.status === "completed" && i.completed_at &&
      daysSince(i.completed_at) <= 7).length,
    [items],
  );

  // Text/person filter applies to the visible groups
  const filterFn = (i: WaitingItem) => {
    if (personFilter !== "all" && i.person_type !== personFilter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      i.person_name.toLowerCase().includes(q) ||
      i.reason.toLowerCase().includes(q) ||
      (i.category ?? "").toLowerCase().includes(q) ||
      (i.related_email ?? "").toLowerCase().includes(q)
    );
  };

  const groups = useMemo(() => {
    const g: Record<GroupKey, WaitingItem[]> = {
      needs_me: [], waiting_others: [], due_soon: [], finished: [], history: [],
    };
    for (const i of items.filter(filterFn)) g[assignGroup(i)].push(i);
    // Sort each: oldest / soonest first
    g.needs_me.sort((a, b) => (isOverdue(b) ? 1 : 0) - (isOverdue(a) ? 1 : 0) ||
      new Date(a.date_requested).getTime() - new Date(b.date_requested).getTime());
    g.due_soon.sort((a, b) => (daysUntilDue(a) ?? 99) - (daysUntilDue(b) ?? 99));
    g.waiting_others.sort((a, b) => new Date(a.date_requested).getTime() - new Date(b.date_requested).getTime());
    g.finished.sort((a, b) => new Date(b.completed_at ?? b.updated_at).getTime() - new Date(a.completed_at ?? a.updated_at).getTime());
    g.history.sort((a, b) => new Date(b.completed_at ?? b.updated_at).getTime() - new Date(a.completed_at ?? a.updated_at).getTime());
    return g;
  }, [items, search, personFilter]);

  const summaryStats: SummaryStat[] = [
    { label: "Waiting on Me", value: waitingOnMe.length, hideIfZero: true },
    { label: "Overdue", value: overdue.length, hideIfZero: true },
    { label: "Due this week", value: stats.dueThisWeek, hideIfZero: true },
    { label: "No follow-up scheduled", value: noFollowUp.length, hideIfZero: true },
    { label: "Wrapped up this week", value: finishedRecently, hideIfZero: true },
  ];

  // Today's Focus — up to three decisions
  const decisions = useMemo(() => {
    const out: JSX.Element[] = [];
    if (overdue.length > 0) {
      const first = overdue[0];
      out.push(
        <DecisionCard
          key="overdue"
          phase="needs_care"
          decision={overdue.length === 1
            ? `Follow up with ${first.person_name}`
            : `Follow up on ${overdue.length} overdue items`}
          because={overdue.length === 1
            ? first.reason
            : `Oldest is ${daysSince(first.date_requested)} days past due.`}
          primaryLabel={overdue.length === 1 ? "Send Follow-Up" : "Open the queue"}
          onPrimary={() => overdue.length === 1 ? setDetailId(first.id) : document.getElementById("group-needs_me")?.scrollIntoView({ behavior: "smooth" })}
          evidence={
            <ul className="list-disc pl-4 space-y-1">
              {overdue.slice(0, 6).map((i) => (
                <li key={i.id}>
                  {i.person_name} — {i.reason} · {daysSince(i.date_requested)} days waiting
                </li>
              ))}
            </ul>
          }
        />,
      );
    }
    if (staleCount > 0) {
      out.push(
        <DecisionCard
          key="stale"
          phase="thriving"
          decision={`Close ${staleCount} item${staleCount === 1 ? "" : "s"} already finished at the source`}
          because="The underlying record already shows completion (W-9 received, payment paid, etc.)."
          primaryLabel={reconciling ? "Closing…" : "Close them"}
          onPrimary={closeStale}
        />,
      );
    }
    if (out.length < 3 && noFollowUp.length > 0) {
      out.push(
        <DecisionCard
          key="no-followup"
          phase="waiting"
          decision={`Set a follow-up date on ${noFollowUp.length} item${noFollowUp.length === 1 ? "" : "s"}`}
          because="Without a date, these fall through the cracks."
          primaryLabel="Open the list"
          onPrimary={() => document.getElementById("group-no_followup")?.scrollIntoView({ behavior: "smooth" })}
          evidence={
            <ul className="list-disc pl-4 space-y-1">
              {noFollowUp.slice(0, 6).map((i) => (
                <li key={i.id}>{i.person_name} — {i.reason}</li>
              ))}
            </ul>
          }
        />,
      );
    }
    return out.slice(0, 3);
  }, [overdue, staleCount, noFollowUp, reconciling]);

  const detail = items.find((i) => i.id === detailId) ?? null;
  const activeCount = active.length;
  const whatChanged = finishedRecently > 0
    ? `${finishedRecently} item${finishedRecently === 1 ? "" : "s"} wrapped up in the last week.`
    : "Nothing new since you were last here.";
  const needsAttention = overdue.length > 0
    ? `${overdue.length} overdue · ${waitingOnMe.length} waiting on you`
    : activeCount === 0
      ? "All clear — nothing waiting."
      : "Nothing urgent right now.";
  const doNext = decisions.length > 0
    ? (decisions[0].props as any).decision
    : "Add a new waiting item to keep something on your radar.";

  return (
    <ExecutivePage section="waiting_on">
      <UnseenReviewBar
        badge="waiting_on"
        note="Marking these read only clears the sidebar bubble — the items stay open until they are actually resolved."
      />

      <PageHero
        eyebrow="🌿 Waiting On"
        title="Everything you're waiting for, in one calm place."
        subtitle="Items resolve themselves when the underlying record changes. Nothing lingers."
        actions={
          <>
            {staleCount > 0 && (
              <Button variant="ghost" onClick={closeStale} disabled={reconciling}>
                <Sparkles className="h-4 w-4 mr-1" />
                Tidy {staleCount}
              </Button>
            )}
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4 mr-1" /> New Waiting Item
            </Button>
          </>
        }
      />

      <ThreeQuestions
        whatChanged={whatChanged}
        needsAttention={needsAttention}
        doNext={doNext}
        attentionCount={overdue.length + waitingOnMe.length}
      />

      <ExecutiveSummary stats={summaryStats} />

      {decisions.length > 0 && (
        <QuietSection
          eyebrow="Today's Focus"
          title="Three things worth your attention"
          description="Ranked by what's overdue, what blocks other work, and what you can finish today."
          paper={false}
        >
          <div className="grid gap-3 md:grid-cols-3">{decisions}</div>
        </QuietSection>
      )}

      <QuietSection
        eyebrow="Filters"
        title="Search the queue"
        paper
      >
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by person, reason, category, email…"
              className="pl-8"
            />
          </div>
          <Select value={personFilter} onValueChange={setPersonFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All person types</SelectItem>
              {PERSON_TYPES.map((p) => (
                <SelectItem key={p} value={p}>{p.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </QuietSection>

      <div id="group-needs_me" />
      <Group
        eyebrow="🪴 Needs me today"
        title="Waiting on you"
        empty="Nothing needs you today."
        items={groups.needs_me}
        onOpen={setDetailId}
        onChanged={refresh}
        phase="needs_care"
      />

      <div id="group-no_followup" />
      <Group
        eyebrow="⏳ Waiting on someone else"
        title="Waiting on others"
        empty="No open follow-ups."
        items={groups.waiting_others}
        onOpen={setDetailId}
        onChanged={refresh}
        phase="waiting"
      />

      <Group
        eyebrow="🌱 Due soon"
        title="Coming up in the next few days"
        empty="Nothing due in the next few days."
        items={groups.due_soon}
        onOpen={setDetailId}
        onChanged={refresh}
        phase="growing"
      />

      <Group
        eyebrow="🌿 Finished recently"
        title="Wrapped up in the last week"
        empty="Nothing wrapped up this week yet."
        items={groups.finished}
        onOpen={setDetailId}
        onChanged={refresh}
        phase="thriving"
        muted
      />

      <QuietSection
        eyebrow="History"
        title="Everything else"
        description="Older completed and cancelled items, kept for the record."
        paper
      >
        {groups.history.length === 0 ? (
          <QuietEmptyState message="Your history is empty." />
        ) : (
          <div className="divide-y">
            {groups.history.slice(0, 40).map((i) => (
              <Row key={i.id} item={i} onOpen={() => setDetailId(i.id)} onChanged={refresh} compact />
            ))}
            {groups.history.length > 40 && (
              <p className="pt-3 text-xs text-muted-foreground">
                Showing 40 of {groups.history.length}.
              </p>
            )}
          </div>
        )}
      </QuietSection>

      <WorkflowDraftsWidget />

      {creating && <CreateDialog onClose={() => setCreating(false)} onCreated={refresh} />}
      {detail && <DetailDialog item={detail} onClose={() => setDetailId(null)} onChanged={refresh} />}
    </ExecutivePage>
  );
}

// ────────────────────────── URL helper
function useUrlItemFocus() {
  const [params, setParams] = useSearchParams();
  const setId = (id: string | null) => {
    if (id) params.set("item", id); else params.delete("item");
    setParams(params, { replace: true });
  };
  return [params.get("item"), setId] as const;
}

// ────────────────────────── group + row

function Group({
  eyebrow, title, items, empty, onOpen, onChanged, phase, muted,
}: {
  eyebrow: string; title: string; empty: string; muted?: boolean;
  items: WaitingItem[]; onOpen: (id: string) => void; onChanged: () => void;
  phase: "growing" | "waiting" | "needs_care" | "thriving" | "wrapped_up";
}) {
  return (
    <QuietSection
      eyebrow={eyebrow}
      title={
        <span className={muted ? "text-muted-foreground" : undefined}>{title}</span>
      }
      right={<GardenStatusBadge phase={phase} label={`${items.length}`} />}
      paper
    >
      {items.length === 0 ? (
        <QuietEmptyState message={empty} />
      ) : (
        <div className="divide-y">
          {items.map((i) => (
            <Row key={i.id} item={i} onOpen={() => onOpen(i.id)} onChanged={onChanged} />
          ))}
        </div>
      )}
    </QuietSection>
  );
}

function Row({
  item, onOpen, onChanged, compact,
}: {
  item: WaitingItem; onOpen: () => void; onChanged: () => void; compact?: boolean;
}) {
  const overdue = isOverdue(item);
  const due = daysUntilDue(item);
  const days = daysSince(item.date_requested);
  const href = relatedHref(item);

  const doAndRefresh = async (fn: () => Promise<any>, ok?: string) => {
    try { await fn(); if (ok) toast({ title: ok }); onChanged(); }
    catch (e: any) { toast({ title: "Couldn't update", description: e?.message, variant: "destructive" }); }
  };

  const complete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await doAndRefresh(async () => {
      await supabase.from("waiting_items" as any).update({
        status: "completed",
        completed_at: new Date().toISOString(),
        completion_method: "manual",
      }).eq("id", item.id);
      await supabase.from("waiting_item_events" as any).insert({
        waiting_item_id: item.id, event_type: "completed", actor_type: "admin",
        message: "Marked complete by admin",
      });
    }, "Marked complete");
  };

  const sendFollowUp = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await doAndRefresh(async () => {
      await supabase.from("waiting_items" as any).update({
        status: "reminder_sent", next_reminder_at: null,
      }).eq("id", item.id);
      await supabase.from("waiting_item_events" as any).insert({
        waiting_item_id: item.id, event_type: "reminder_sent", actor_type: "admin",
        message: "Follow-up logged",
      });
    }, "Follow-up logged");
  };

  const flipWaitingOn = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const nextType = isWaitingOnMe(item) ? "parent" : "staff";
    await doAndRefresh(async () => {
      await supabase.from("waiting_items" as any).update({ person_type: nextType }).eq("id", item.id);
      await supabase.from("waiting_item_events" as any).insert({
        waiting_item_id: item.id, event_type: "note", actor_type: "admin",
        message: `Reassigned — now waiting on ${nextType === "staff" ? "me" : item.person_name}`,
      });
    }, "Reassigned");
  };

  const rowActive = item.status !== "completed" && item.status !== "cancelled";

  return (
    <button
      onClick={onOpen}
      className={`w-full text-left flex items-center gap-3 py-3 hover:bg-muted/40 transition-colors ${compact ? "px-1" : "px-2"}`}
    >
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading text-base text-foreground break-words">
            {item.reason}
          </span>
          {overdue && rowActive && (
            <GardenStatusBadge phase="needs_care" label="Overdue" />
          )}
          {isBlocker(item) && rowActive && (
            <Badge variant="outline" className="text-[10px]">Blocking</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed break-words">
          Owner: <span className="text-foreground">{ownerLabel(item)}</span>
          {item.category && <> · {item.category}</>}
          {item.expected_response_date && (
            <> · due {new Date(item.expected_response_date).toLocaleDateString()}
              {due !== null && rowActive && (
                <span className={overdue ? " text-terracotta" : ""}>
                   {" "}({overdue ? `${Math.abs(due)} ${Math.abs(due) === 1 ? "day" : "days"} overdue` : due === 0 ? "today" : `in ${due} ${due === 1 ? "day" : "days"}`})
                </span>
              )}
            </>
          )}
          {!item.expected_response_date && rowActive && <> · no follow-up scheduled</>}
          <> · waiting {days} {days === 1 ? "day" : "days"}</>
        </p>
      </div>
      {rowActive && (
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" onClick={sendFollowUp} title="Send Follow-Up">
            <Bell className="h-4 w-4" />
          </Button>
          <DueDatePopover item={item} onChanged={onChanged} />
          <Button size="sm" variant="ghost" onClick={flipWaitingOn}
            title={isWaitingOnMe(item) ? "Waiting on someone else" : "Waiting on me"}>
            <ArrowRightLeft className="h-4 w-4" />
          </Button>
          {href && (
            <Button size="sm" variant="ghost" asChild title="Open related record" onClick={(e) => e.stopPropagation()}>
              <a href={href}><ExternalLink className="h-4 w-4" /></a>
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={complete} title="Mark Complete">
            <Check className="h-4 w-4" />
          </Button>
        </div>
      )}
    </button>
  );
}

function DueDatePopover({ item, onChanged }: { item: WaitingItem; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(
    item.expected_response_date ? item.expected_response_date.slice(0, 10) : "",
  );
  const save = async () => {
    try {
      await supabase.from("waiting_items" as any).update({
        expected_response_date: value ? new Date(value).toISOString() : null,
      }).eq("id", item.id);
      await supabase.from("waiting_item_events" as any).insert({
        waiting_item_id: item.id, event_type: "note", actor_type: "admin",
        message: value ? `Due date set to ${value}` : "Due date cleared",
      });
      toast({ title: "Due date updated" });
      setOpen(false);
      onChanged();
    } catch (e: any) {
      toast({ title: "Couldn't update", description: e?.message, variant: "destructive" });
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost" title="Change Due Date" onClick={(e) => e.stopPropagation()}>
          <CalendarIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2" onClick={(e) => e.stopPropagation()}>
        <label className="text-xs font-medium">Follow up on…</label>
        <Input type="date" value={value} onChange={(e) => setValue(e.target.value)} />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" onClick={save}>Set Follow-up</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ────────────────────────── create + detail (preserved from prior implementation)

function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [personType, setPersonType] = useState("other");
  const [personName, setPersonName] = useState("");
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [expected, setExpected] = useState("");
  const [reminderDays, setReminderDays] = useState("3");
  const [priority, setPriority] = useState<"low" | "normal" | "high" | "critical">("normal");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!personName.trim() || !reason.trim()) {
      toast({ title: "Person and reason are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { data: session } = await supabase.auth.getUser();
    const rd = Number(reminderDays);
    const nextReminder = rd > 0 ? new Date(Date.now() + rd * DAY).toISOString() : null;
    const { error } = await supabase.from("waiting_items" as any).insert({
      created_by: session.user?.id,
      person_type: personType,
      person_name: personName.trim(),
      reason: reason.trim(),
      category: category.trim() || null,
      related_email: email.trim() || null,
      related_phone: phone.trim() || null,
      expected_response_date: expected ? new Date(expected).toISOString() : null,
      reminder_interval_days: rd > 0 ? rd : null,
      next_reminder_at: nextReminder,
      priority,
      internal_notes: notes.trim() || null,
    });
    setSaving(false);
    if (error) {
      friendlyError({ what: `I couldn't add ${personName.trim()} to your Waiting list.`, technical: error });
      return;
    }
    toast({ title: "Waiting item created" });
    onCreated(); onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Waiting Item</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium">Person type</label>
              <Select value={personType} onValueChange={setPersonType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERSON_TYPES.map((p) => (
                    <SelectItem key={p} value={p}>{p.replace("_", " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Priority</label>
              <Select value={priority} onValueChange={(v) => setPriority(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium">Person name *</label>
            <Input value={personName} onChange={(e) => setPersonName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium">What are you waiting for? *</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium">Category</label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. w9, vendor_fee" />
            </div>
            <div>
              <label className="text-xs font-medium">Follow up on…</label>
              <Input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium">Email</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium">Phone</label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium">Reminder every (days)</label>
            <Input type="number" min={0} value={reminderDays} onChange={(e) => setReminderDays(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium">Internal notes</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({
  item, onClose, onChanged,
}: {
  item: WaitingItem; onClose: () => void; onChanged: () => void;
}) {
  const [events, setEvents] = useState<any[]>([]);
  const [noteText, setNoteText] = useState("");
  const href = relatedHref(item);

  useEffect(() => {
    supabase.from("waiting_item_events" as any)
      .select("*").eq("waiting_item_id", item.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => setEvents(data ?? []));
  }, [item.id]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    await supabase.from("waiting_item_events" as any).insert({
      waiting_item_id: item.id, event_type: "note", actor_type: "admin",
      message: noteText.trim(),
    });
    setNoteText("");
    const { data } = await supabase.from("waiting_item_events" as any)
      .select("*").eq("waiting_item_id", item.id)
      .order("created_at", { ascending: false });
    setEvents(data ?? []);
  };

  const duplicate = async () => {
    const { data: session } = await supabase.auth.getUser();
    await supabase.from("waiting_items" as any).insert({
      created_by: session.user?.id,
      person_type: item.person_type, person_name: item.person_name,
      reason: item.reason, category: item.category,
      related_email: item.related_email, related_phone: item.related_phone,
      priority: item.priority,
      related_instructor_id: item.related_instructor_id,
      related_vendor_id: item.related_vendor_id,
    });
    toast({ title: "Duplicated" });
    onChanged(); onClose();
  };

  const archive = async () => {
    await supabase.from("waiting_items" as any).update({ status: "cancelled" }).eq("id", item.id);
    await supabase.from("waiting_item_events" as any).insert({
      waiting_item_id: item.id, event_type: "cancelled", actor_type: "admin",
    });
    toast({ title: "Archived" });
    onChanged(); onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{item.reason}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline">Owner: {ownerLabel(item)}</Badge>
            <Badge variant="outline">{item.person_type.replace("_", " ")}</Badge>
            <Badge variant="outline">{item.priority} priority</Badge>
            {item.category && <Badge variant="secondary">{item.category}</Badge>}
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
            <div>Requested: {new Date(item.date_requested).toLocaleDateString()}</div>
            {item.expected_response_date && (
              <div>Follow up: {new Date(item.expected_response_date).toLocaleDateString()}</div>
            )}
            {item.related_email && <div>Email: {item.related_email}</div>}
            {item.related_phone && <div>Phone: {item.related_phone}</div>}
            {item.completed_at && <div>Completed: {new Date(item.completed_at).toLocaleString()}</div>}
            {item.completion_method && <div>Via: {item.completion_method}</div>}
          </div>

          {href && (
            <Button asChild size="sm" variant="outline">
              <a href={href}><ExternalLink className="h-3 w-3 mr-1" /> Open related record</a>
            </Button>
          )}

          {item.internal_notes && (
            <div className="rounded-md border bg-muted/40 p-2 text-sm whitespace-pre-wrap">
              {item.internal_notes}
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Timeline</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {events.length === 0 ? (
                <p className="text-xs text-muted-foreground">No events yet.</p>
              ) : (events.map((e) => (
                <div key={e.id} className="text-xs border-l-2 border-primary/40 pl-2">
                  <div className="font-medium">
                    {new Date(e.created_at).toLocaleString()} — {e.event_type}
                    <span className="ml-1 text-muted-foreground">({e.actor_type})</span>
                  </div>
                  {e.message && <div className="text-muted-foreground">{e.message}</div>}
                </div>
              )))}
            </div>
          </div>

          <div className="flex gap-2">
            <Input value={noteText} onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note to the timeline…" />
            <Button onClick={addNote} size="sm">Add</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={duplicate}>
            <Copy className="h-3 w-3 mr-1" /> Duplicate
          </Button>
          <Button variant="ghost" size="sm" onClick={archive}>Archive</Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
